import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  CORE_POV_IDS,
  countFrames,
  estimateMicros,
  planInputOf,
  type Attitude,
  type Expression,
  NO_STOCK_ID,
  POSE_CATEGORIES,
  resolveStock,
  type PoseCategory,
  type FashionModel,
  type Garment,
  type ShootPreset,
  type ShootSettings,
  ACCESSORY_PRESETS,
  AUTO_SCENE_ID,
  EXPRESSIONS,
  HOUSE_RECIPES,
  type HouseRecipe,
  ATTITUDES,
} from '@studio/shared';

import { api, type Presets } from '../lib/api';
import { GarmentUploader } from '../components/GarmentUploader';
import { RatioPicker } from '../components/RatioPicker';
import { GradePicker, LightPicker, StockPicker } from '../components/LookPicker';
import { PosePicker } from '../components/PosePicker';
import { PovPicker } from '../components/PovPicker';
import { ScenePicker } from '../components/ScenePicker';
import { Segmented } from '../components/Segmented';
import { Group } from '../components/Group';
import { Planche } from '../components/Planche';
import { VoiceInput } from '../components/VoiceInput';
import { SparkIcon } from '../components/Icons';
import { useDialog } from '../components/Dialog';
import { useToast } from '../components/Toaster';
import { useDateFormat } from '../lib/dates';
import { usePresetLabel, useT } from '../lib/i18n';
import { useMoney } from '../lib/money';
import { useErrorText } from '../lib/errors';
import { DEFAULT_SETTINGS, defaultName } from '../lib/shootDefaults';
import { ModelChooser } from '../components/ModelChooser';

export function NewShootPage() {
  const navigate = useNavigate();

  const [presets, setPresets] = useState<Presets | undefined>();
  const [models, setModels] = useState<FashionModel[]>([]);
  const [garments, setGarments] = useState<Garment[]>([]);
  const [recipes, setRecipes] = useState<ShootPreset[]>([]);
  const [applied, setApplied] = useState('');

  const [name, setName] = useState('');
  const [modelIds, setModelIds] = useState<string[]>([]);
  const [povIds, setPovIds] = useState<string[]>(['full-front']);
  const [settings, setSettings] = useState<ShootSettings>(DEFAULT_SETTINGS);
  const [showAdvanced, setShowAdvanced] = useState(false);

  const toast = useToast();
  const { prompt } = useDialog();
  const t = useT();
  const when = useDateFormat();
  const money = useMoney();
  const describe = useErrorText();
  const tp = usePresetLabel();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    // Steps decide whether to start folded from whether they already have an
    // answer, and they read that once — so nothing renders until the data that
    // answers them has arrived.
    void Promise.all([
      api.presets().then(setPresets),
      api.listShootPresets().then(setRecipes),
      api.listModels().then((list) => {
        setModels(list);
        // With exactly one model there is nothing to choose — preselect it.
        if (list.length === 1 && list[0]) setModelIds([list[0].id]);
      }),
    ])
      .catch(() => undefined)
      .finally(() => setLoaded(true));
  }, []);

  /*
   * No count of takes to set: each position chosen is shot once on each
   * angle, and each scene, attitude and expression chosen multiplies that.
   * What is chosen is the roll, and the number follows from it.
   */
  const plan: ShootSettings = { ...settings, variantsPerPov: Math.max(1, settings.poseIds.length) };
  const shotCount = countFrames(planInputOf(plan, modelIds, povIds));
  const scenes = settings.backdropIds?.length ? settings.backdropIds : [settings.backdropId];
  const attitudes: Attitude[] = settings.attitudes?.length
    ? settings.attitudes
    : [settings.attitude ?? 'editorial'];
  const expressions: Expression[] = settings.expressions?.length
    ? settings.expressions
    : [settings.expression ?? 'auto'];
  // Zero means the server sets no cap, which is the default.
  const maxFrames = presets?.maxFramesPerShoot ?? 0;
  const overLimit = maxFrames > 0 && shotCount > maxFrames;
  // Every frame is a paid render, so make the multiplication visible before
  // the click rather than after the bill.
  const heavy = shotCount > 24 && !overLimit;
  const ready = modelIds.length > 0 && povIds.length > 0 && garments.length > 0 && !overLimit;

  const set = <K extends keyof ShootSettings>(key: K, value: ShootSettings[K]) =>
    setSettings((prev) => ({ ...prev, [key]: value }));

  /**
   * A location carries its own daylight; a softbox on a clifftop makes no
   * sense. With several scenes the first is the roll's own (and lights it);
   * each frame in another takes that one's light on the server.
   */
  const chooseScenes = (ids: string[]) => {
    const first = ids[0] ?? settings.backdropId;
    const scene = presets?.backdrops.find((b) => b.id === first);
    setSettings((prev) => ({
      ...prev,
      backdropId: first,
      backdropIds: ids.length > 1 ? ids : undefined,
      lightingId: scene?.suggestedLightingId ?? prev.lightingId,
    }));
  };

  /** Several of one kind: the first is the roll's own, the list multiplies the roll. */
  const toggleIn = <T extends string>(list: T[], value: T, solo?: T): T[] => {
    if (value === solo) return [value];
    const rest = list.filter((v) => v !== solo);
    const next = rest.includes(value) ? rest.filter((v) => v !== value) : [...rest, value];
    return next.length ? next : list;
  };
  const chooseAttitudes = (list: Attitude[]) =>
    setSettings((prev) => ({ ...prev, attitude: list[0], attitudes: list.length > 1 ? list : undefined }));
  const chooseExpressions = (list: Expression[]) =>
    setSettings((prev) => ({
      ...prev,
      expression: list[0],
      expressions: list.length > 1 ? list : undefined,
    }));

  /**
   * Replays a saved recipe onto the form.
   *
   * A model that has since been deleted is dropped rather than carried as a
   * dead id — the roll would fail at the first frame otherwise, and the
   * honest thing is to show the cast that is actually left.
   */
  const applyRecipe = (recipe: ShootPreset) => {
    const live = recipe.modelIds.filter((id) => models.some((m) => m.id === id));
    setModelIds(live);
    setPovIds(recipe.povIds);
    setSettings(recipe.settings);
    setApplied(recipe.id);
    toast(
      live.length < recipe.modelIds.length
        ? t('shoot.recipeAppliedPartial', { name: recipe.name })
        : t('shoot.recipeApplied', { name: recipe.name }),
      'ok',
    );
  };

  /* A house look keeps whoever is already cast: it is about the frames and
     the styling, never about the face. */
  const applyHouseRecipe = (recipe: HouseRecipe) => {
    setPovIds(recipe.povIds);
    setSettings({ ...DEFAULT_SETTINGS, ...recipe.settings });
    setApplied(recipe.id);
    toast(t('shoot.recipeApplied', { name: tp(`recipe.${recipe.id}.name`, recipe.name) }), 'ok');
  };

  const saveRecipe = async () => {
    const label = await prompt({
      title: t('shoot.recipeName'),
      label: t('shoot.recipeNameLabel'),
      value: name.trim() || t('shoot.recipeDefault'),
      confirmLabel: t('common.save'),
    });
    if (!label?.trim()) return;
    try {
      const saved = await api.createShootPreset({ name: label.trim(), modelIds, povIds, settings: plan });
      setRecipes((prev) => [saved, ...prev]);
      setApplied(saved.id);
      toast(t('shoot.recipeSaved', { name: saved.name }), 'ok');
    } catch (err) {
      toast(describe(err, 'shoot.failed'), 'error');
    }
  };

  const forgetRecipe = async (id: string) => {
    setRecipes((prev) => prev.filter((r) => r.id !== id));
    if (applied === id) setApplied('');
    await api.deleteShootPreset(id).catch(() => undefined);
  };

  const submit = async () => {
    setSubmitting(true);
    setError('');
    try {
      const shoot = await api.createShoot({
        name: name.trim() || defaultName(garments, t, when.short),
        modelIds,
        garmentIds: garments.map((g) => g.id),
        povIds,
        settings: { ...plan, mode: 'presets' },
        start: true,
      });
      toast(
        t(shotCount === 1 ? 'shoot.rollingOne' : 'shoot.rolling', {
          n: shotCount,
          name: shoot.name,
        }),
        'ok',
      );
      navigate(`/shoots/${shoot.id}`);
    } catch (err) {
      setError(describe(err, 'shoot.failed'));
      setSubmitting(false);
    }
  };

  /** A preset's label in the current language, falling back to its English name. */
  const nameOf = <T extends { id: string; name: string }>(
    list: T[] | undefined,
    id: string,
    kind: string,
  ) => {
    const found = list?.find((x) => x.id === id);
    return found ? tp(`${kind}.${found.id}.name`, found.name) : '—';
  };

  /*
   * The short lists. Fifteen camera angles and seventy poses were a catalogue,
   * not a choice; the composer offers the eight angles and the poses of its
   * three families, and anything already chosen — a saved recipe, an old roll
   * — stays visible so it can be seen and removed.
   */
  const corePovs = (presets?.povs ?? []).filter((p) => CORE_POV_IDS.includes(p.id) || povIds.includes(p.id));
  const curatedPoses = (Object.keys(POSE_CATEGORIES) as PoseCategory[]).flatMap((category) =>
    POSE_CATEGORIES[category]
      .map((id) => presets?.poses.find((p) => p.id === id))
      .filter((p): p is NonNullable<typeof p> => Boolean(p))
      // Grouped by family on the chips: the picker reads the group to label them.
      .map((p) => ({ ...p, group: category as unknown as typeof p.group })),
  );

  const sceneName =
    scenes.length > 1
      ? t('plan.n.scenes', { n: scenes.length })
      : settings.backdropId === AUTO_SCENE_ID
        ? t('scene.auto')
        : nameOf(presets?.backdrops, settings.backdropId, 'scene');
  const lightName = nameOf(presets?.lighting, settings.lightingId, 'light');
  const gradeName = nameOf(presets?.grades, settings.gradeId, 'grade');
  const stock = resolveStock(settings.filmStockId);
  const stockName = tp(`stock.${stock.id}.name`, stock.name);

  /** The multiplication, in words: "3 angles × 2 positions × 2 scenes". */
  const breakdown = [
    modelIds.length > 1 ? t('plan.n.models', { n: modelIds.length }) : '',
    t(povIds.length === 1 ? 'plan.n.angle' : 'plan.n.angles', { n: povIds.length }),
    settings.poseIds.length > 1 ? t('plan.n.positions', { n: settings.poseIds.length }) : '',
    scenes.length > 1 ? t('plan.n.scenes', { n: scenes.length }) : '',
    attitudes.length > 1 ? t('plan.n.attitudes', { n: attitudes.length }) : '',
    expressions.length > 1 ? t('plan.n.expressions', { n: expressions.length }) : '',
  ]
    .filter(Boolean)
    .join(' × ');

  /** The one thing standing between here and a shoot, if anything is. */
  const blocker = overLimit
    ? t('shoot.blocked.limit', { n: maxFrames })
    : modelIds.length === 0
      ? t('shoot.blocked.model')
      : garments.length === 0
        ? t('shoot.blocked.garments')
        : povIds.length === 0
          ? t('shoot.blocked.povs')
          : '';

  return (
    <div className="composer composer--presets">
      {/*
       * The slate is the page's title rather than the eighth thing on a list.
       * It has a sensible default, so it is a thing you may rename, not a
       * field you must fill.
       */}
      <header className="composer__head">
        <div className="composer__slate">
          <span className="label">{t('shoot.eyebrow')}</span>
          <input
            className="composer__name"
            value={name}
            placeholder={defaultName(garments, t, when.short)}
            aria-label={t('shoot.name')}
            onChange={(event) => setName(event.target.value)}
          />
        </div>

        {loaded && (
          <div className="composer__recipes">
            <span className="label">{t('shoot.recipes')}</span>
            {HOUSE_RECIPES.map((recipe) => (
              <span key={recipe.id} className={`recipes__chip ${applied === recipe.id ? 'is-on' : ''}`}>
                <button
                  type="button"
                  title={tp(`recipe.${recipe.id}.desc`, recipe.description)}
                  onClick={() => applyHouseRecipe(recipe)}
                >
                  {tp(`recipe.${recipe.id}.name`, recipe.name)}
                </button>
              </span>
            ))}
            {recipes.map((recipe) => (
              <span key={recipe.id} className={`recipes__chip ${applied === recipe.id ? 'is-on' : ''}`}>
                <button type="button" onClick={() => applyRecipe(recipe)}>
                  {recipe.name}
                </button>
                <button
                  type="button"
                  className="recipes__drop"
                  aria-label={t('shoot.recipeForget', { name: recipe.name })}
                  onClick={() => void forgetRecipe(recipe.id)}
                >
                  ×
                </button>
              </span>
            ))}
          </div>
        )}
      </header>

      {error && <div className="banner banner--error">{error}</div>}

      {!loaded ? (
        <p className="muted">{t('shoot.loadingStudio')}</p>
      ) : (
        <div className="composer__panes">
          {/* ---------------------------- decisions ---------------------------- */}
          <div className="composer__decide">
            <Group
              n={1}
              title={t('shoot.group.subject')}
              done={modelIds.length > 0 && garments.length > 0}
              aside={
                models.length > 1 && modelIds.length > 0 ? (
                  <span className="badge">
                    {t('shoot.castOf', { done: modelIds.length, total: models.length })}
                  </span>
                ) : undefined
              }
            >
              <div className="stack stack--lg">
                <div className="stack stack--sm">
                  <span className="label">{t('shoot.who')}</span>
                  <ModelChooser models={models} selected={modelIds} onChange={setModelIds} />
                </div>

                <div className="stack stack--sm">
                  <span className="label">{t('shoot.what')}</span>
                  <GarmentUploader garments={garments} onChange={setGarments} />
                </div>

                {/*
                 * How far the stylist goes. One photographed piece and
                 * "complete the look" is a whole outfit chosen around it;
                 * the default dresses the model in exactly what is here.
                 */}
                <div className="stack stack--sm">
                  <span className="label">{t('shoot.styling')}</span>
                  <Segmented
                    options={['as-uploaded', 'complete'] as const}
                    value={settings.stylingMode ?? 'as-uploaded'}
                    onChange={(mode) => set('stylingMode', mode)}
                    label={t('shoot.styling')}
                    format={(mode) => t(`enum.styling.${mode}`)}
                  />
                  <p className="faint">
                    {t(
                      settings.stylingMode === 'complete'
                        ? 'shoot.stylingCompleteHint'
                        : 'shoot.stylingUploadedHint',
                    )}
                  </p>
                </div>

                <div className="stack stack--sm">
                  <span className="label">{t('shoot.accessories')}</span>
                  <div className="row row--wrap" style={{ gap: 6 }}>
                    {ACCESSORY_PRESETS.map((accessory) => {
                      const chosen = settings.accessories ?? [];
                      const on = chosen.includes(accessory.id);
                      return (
                        <button
                          key={accessory.id}
                          type="button"
                          className={`chip ${on ? 'is-on' : ''}`}
                          aria-pressed={on}
                          onClick={() =>
                            set(
                              'accessories',
                              on ? chosen.filter((id) => id !== accessory.id) : [...chosen, accessory.id],
                            )
                          }
                        >
                          {tp(`accessory.${accessory.id}`, accessory.name)}
                        </button>
                      );
                    })}
                  </div>
                  <div className="field">
                    <label htmlFor="accessory-notes">{t('shoot.accessoriesOther')}</label>
                    <input
                      id="accessory-notes"
                      className="input"
                      value={settings.accessoryNotes ?? ''}
                      placeholder={t('shoot.accessoriesPlaceholder')}
                      onChange={(event) => set('accessoryNotes', event.target.value)}
                    />
                  </div>
                  <p className="faint">{t('shoot.accessoriesHint')}</p>
                </div>
              </div>
            </Group>

            <Group
              n={2}
              title={t('shoot.group.frames')}
              done={povIds.length > 0}
              aside={
                <span className="badge">
                  {t(povIds.length === 1 ? 'shoot.setUpN' : 'shoot.setUpsN', { n: povIds.length })}
                </span>
              }
            >
              <div className="stack stack--lg">
                {presets && (
                  <div className="stack stack--sm">
                    <PovPicker povs={corePovs} selected={povIds} onChange={setPovIds} />
                  </div>
                )}

                <Group
                  title={t('shoot.step.positions')}
                  hint={t('shoot.optional')}
                  foldable
                  startFolded={settings.poseIds.length === 0}
                  done={settings.poseIds.length > 0}
                  summary={
                    settings.poseIds.length
                      ? settings.poseIds.map((id) => nameOf(presets?.poses, id, 'pose')).join(' · ')
                      : t('shoot.positionsDefault')
                  }
                >
                  {presets && (
                    <>
                      <PosePicker
                        poses={curatedPoses}
                        selected={settings.poseIds}
                        onChange={(ids) => set('poseIds', ids)}
                        variants={plan.variantsPerPov}
                      />
                    </>
                  )}
                </Group>
              </div>
            </Group>

            <Group
              n={3}
              title={t('shoot.group.look')}
              hint={t('shoot.optional')}
              foldable
              startFolded
              done
              summary={[sceneName, lightName, gradeName, stockName].join(' · ')}
            >
              <div className="stack stack--lg">
                <div className="stack stack--sm">
                  <span className="label">{t('shoot.where')}</span>
                  {presets && (
                    <ScenePicker
                      scenes={presets.backdrops}
                      selected={settings.backdropId}
                      onChange={(id) => chooseScenes([id])}
                      many={{ selected: scenes, onChange: chooseScenes }}
                      custom={settings.customBackdrop}
                      onCustomChange={(text) => set('customBackdrop', text)}
                      backgroundAssetId={settings.backgroundAssetId}
                      onBackgroundChange={(id) => set('backgroundAssetId', id)}
                    />
                  )}
                </div>

                <div className="stack stack--sm">
                  <span className="label">{t('shoot.expression')}</span>
                  <div
                    className="row row--wrap"
                    style={{ gap: 6 }}
                    role="group"
                    aria-label={t('shoot.expression')}
                  >
                    {EXPRESSIONS.map(({ id }) => (
                      <button
                        key={id}
                        type="button"
                        className={`chip ${expressions.includes(id) ? 'is-on' : ''}`}
                        aria-pressed={expressions.includes(id)}
                        onClick={() => chooseExpressions(toggleIn(expressions, id, 'auto'))}
                      >
                        {t(`enum.expression.${id}`)}
                      </button>
                    ))}
                  </div>
                  <p className="faint">{t('shoot.expressionHint')}</p>
                </div>

                <div className="stack stack--sm">
                  <span className="label">{t('shoot.attitude')}</span>
                  <div
                    className="row row--wrap"
                    style={{ gap: 6 }}
                    role="group"
                    aria-label={t('shoot.attitude')}
                  >
                    {ATTITUDES.map(({ id }) => (
                      <button
                        key={id}
                        type="button"
                        className={`chip ${attitudes.includes(id) ? 'is-on' : ''}`}
                        aria-pressed={attitudes.includes(id)}
                        onClick={() => chooseAttitudes(toggleIn(attitudes, id))}
                      >
                        {t(`enum.attitude.${id}`)}
                      </button>
                    ))}
                  </div>
                  <p className="faint">
                    {attitudes.length > 1
                      ? t('shoot.severalHint')
                      : t(`shoot.attitudeHint.${settings.attitude ?? 'editorial'}`)}
                  </p>
                </div>

                <div className="stack stack--sm">
                  <span className="label">{t('shoot.light')}</span>
                  {presets && (
                    <LightPicker
                      lighting={presets.lighting}
                      selected={settings.lightingId}
                      onChange={(id) => set('lightingId', id)}
                    />
                  )}
                </div>

                <div className="stack stack--sm">
                  <span className="label">{t('shoot.colour')}</span>
                  {presets && (
                    <GradePicker
                      grades={presets.grades}
                      selected={settings.gradeId}
                      onChange={(id) => set('gradeId', id)}
                    />
                  )}
                </div>

                <div className="stack stack--sm">
                  <div className="row row--between">
                    <span className="label">{t('shoot.filmGrain')}</span>
                    <span className="faint">
                      {settings.grain > 0
                        ? t('shoot.grainValue', { n: Math.round(settings.grain) })
                        : t('shoot.noGrain')}
                    </span>
                  </div>
                  {presets && (
                    <StockPicker
                      stocks={presets.stocks}
                      selected={settings.filmStockId}
                      grain={settings.grain}
                      onChange={(next) =>
                        setSettings((prev) => ({ ...prev, filmStockId: next.id, grain: next.grain }))
                      }
                    />
                  )}
                  {settings.filmStockId !== NO_STOCK_ID && (
                    <div className="dial">
                      <label className="dial__label" htmlFor="grain">
                        {t('shoot.grain')}
                      </label>
                      <input
                        id="grain"
                        className="dial__range"
                        type="range"
                        min={0}
                        max={100}
                        step={1}
                        value={settings.grain}
                        onChange={(event) => set('grain', Number(event.target.value))}
                      />
                      <span className="dial__value">{Math.round(settings.grain)}</span>
                    </div>
                  )}
                  <p className="faint">{t('shoot.grainNote')}</p>
                </div>

                <div className="stack stack--sm">
                  <span className="label">{t('shoot.shape')}</span>
                  <RatioPicker
                    ratios={presets?.aspectRatios ?? []}
                    selected={settings.aspectRatio}
                    onChange={(r) => set('aspectRatio', r)}
                  />
                </div>

                <div className="stack stack--sm">
                  <VoiceInput
                    label={t('shoot.notes')}
                    value={settings.details}
                    onChange={(value) => set('details', value)}
                    placeholder={t('shoot.notesPlaceholder')}
                    rows={3}
                  />
                  <p className="faint">{t('shoot.notesHint')}</p>
                </div>

                <button
                  type="button"
                  className="btn btn--link btn--sm"
                  style={{ alignSelf: 'flex-start' }}
                  onClick={() => setShowAdvanced((v) => !v)}
                >
                  {t(showAdvanced ? 'shoot.hideTechnical' : 'shoot.showTechnical')}
                </button>

                {showAdvanced && (
                  <div className="panel panel--inset stack stack--sm">
                    <div className="field">
                      <label htmlFor="copy-space">{t('shoot.copySpace')}</label>
                      <select
                        id="copy-space"
                        className="select"
                        value={settings.copySpace ?? 'none'}
                        onChange={(event) =>
                          set('copySpace', event.target.value as ShootSettings['copySpace'])
                        }
                      >
                        <option value="none">{t('shoot.copy.none')}</option>
                        <option value="left">{t('shoot.copy.left')}</option>
                        <option value="right">{t('shoot.copy.right')}</option>
                        <option value="top">{t('shoot.copy.top')}</option>
                      </select>
                      <p className="faint">{t('shoot.copyHint')}</p>
                    </div>
                    <div className="field">
                      <label htmlFor="style-notes">{t('shoot.styleNotes')}</label>
                      <input
                        id="style-notes"
                        className="input"
                        value={settings.styleNotes}
                        placeholder={t('shoot.styleNotesPlaceholder')}
                        onChange={(event) => set('styleNotes', event.target.value)}
                      />
                    </div>
                  </div>
                )}
              </div>
            </Group>
          </div>

          {/* ------------------------- what you will get ------------------------ */}
          <aside className="composer__plan">
            <div className="plan">
              <div className="plan__figure">
                <span className="plan__count">{shotCount}</span>
                <span className="plan__unit">
                  {t(shotCount === 1 ? 'common.frame' : 'common.frames')}
                  {shotCount > 0 && <> · {money.cost(estimateMicros(shotCount, settings))}</>}
                </span>
              </div>

              <p className="plan__sum">{breakdown}</p>

              <Planche
                modelIds={modelIds}
                povIds={povIds}
                variantsPerPov={plan.variantsPerPov}
                poseIds={settings.poseIds}
                across={{
                  backdropIds: settings.backdropIds,
                  attitudes: settings.attitudes,
                  expressions: settings.expressions,
                }}
                models={models}
                povs={presets?.povs ?? []}
                poses={presets?.poses ?? []}
                aspectRatio={settings.aspectRatio}
              />

              {blocker ? (
                <p className={`plan__blocker ${overLimit ? 'is-over' : ''}`}>{blocker}</p>
              ) : (
                heavy && <p className="plan__blocker is-warn">{t('shoot.longRun')}</p>
              )}

              <button
                className="btn btn--primary btn--block"
                disabled={!ready || submitting}
                onClick={() => void submit()}
              >
                {submitting ? <span className="spinner" /> : <SparkIcon />}
                {t(submitting ? 'shoot.going' : 'shoot.go')}
              </button>

              <button
                type="button"
                className="btn btn--link btn--sm plan__save"
                onClick={() => void saveRecipe()}
              >
                {t('shoot.recipeSave')}
              </button>
            </div>
          </aside>
        </div>
      )}
    </div>
  );
}
